import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';
import './MusicMinistryPage.css';

const ministries = [
  {
    name: 'Parish Choir',
    description:
      'Through weekly rehearsal and Sunday leadership, the choir develops the listening, unity, and musical confidence that help the whole congregation sing.',
  },
  {
    name: 'Handbells',
    description:
      'Handbells cultivate shared pulse, sustained attention, and mutual responsibility while bringing another voice of prayer into worship.',
  },
  {
    name: 'Cantors & Instrumentalists',
    description:
      'Cantors and instrumentalists offer prepared musical leadership for particular services, supporting the congregation’s prayer and inviting its response.',
  },
  {
    name: 'Children & Future Generations',
    description:
      'Children learn to sing by singing among people who sing. Their participation ensures that the music God has given us remains a living inheritance.',
  },
  {
    name: 'Spanish & Bilingual Music',
    description:
      'Our English and Spanish musical lives deepen one another, enlarging the language, rhythm, and repertoire through which the congregation offers praise.',
  },
  {
    name: 'Special Services & Projects',
    description:
      'Taizé, Jazz Vespers, concerts, and larger works extend our common practice. They deepen attention, invite new voices, and return new musical life to Sunday worship.',
  },
];

const formationPrinciples = [
  {
    title: 'Formation before explanation',
    sources: 'Plato · Basil',
    text:
      'Rhythm, melody, and repetition shape attention and desire before we can fully explain what we have learned.',
  },
  {
    title: 'Judgment through participation',
    sources: 'Aristotle',
    text:
      'We learn to hear and discern by singing and making music, not only by receiving ideas about it.',
  },
  {
    title: 'The whole person practices',
    sources: 'Athanasius · Aristides · Augustine · Iamblichus',
    text:
      'Singing joins words, memory, feeling, breath, body, and other people, allowing the whole person to be trained together.',
  },
];

const ministryFramework = [
  {
    label: 'Mission',
    text: 'Why this particular group exists within our common vision.',
  },
  {
    label: 'Service',
    text: 'How its work strengthens the song of the whole congregation.',
  },
  {
    label: 'Practice',
    text: 'What its members discover and develop together.',
  },
  {
    label: 'Commitments',
    text: 'The presence, preparation, attention, and responsibility that sustain the work.',
  },
  {
    label: 'Participation',
    text: 'Who the ministry is for and how someone may begin.',
  },
];

const MusicMinistryPage: React.FC = () => {
  return (
    <div className="music-ministry-page">
      <Navigation />

      <main>
        <header className="music-ministry-hero">
          <div className="music-ministry-hero-inner">
            <p className="music-ministry-eyebrow">St. Luke’s ~ San Lucas</p>
            <h1>Our Musical Life</h1>
            <p className="music-ministry-intro">
              The vision, mission, and shared practices that shape music in our
              congregation.
            </p>
            <p className="music-ministry-draft">Working draft</p>
          </div>
        </header>

        <nav className="music-ministry-section-nav" aria-label="On this page">
          <div>
            <a href="#vision">Vision &amp; Mission</a>
            <a href="#sunday-worship">Sunday Worship</a>
            <a href="#formation">Why Practice Matters</a>
            <a href="#ministries">Our Ministries</a>
            <a href="#shared-shape">Shared Shape</a>
          </div>
        </nav>

        <div className="music-ministry-content">
          <section id="vision" className="vision-mission-grid" aria-labelledby="vision-heading">
            <article className="statement-panel statement-panel-vision">
              <p className="section-kicker">Where we are going</p>
              <h2 id="vision-heading">Music Vision</h2>
              <p>
                Our vision is a church that sings: a congregation in which every
                voice belongs and is needed, and where each voice empowers and
                sustains the others. Singing together, our music opens our
                hearts, lifts our minds, and binds us together as one body. Our
                continuous practice of singing the songs of our faith naturally
                and effortlessly nurtures and develops our own musical and
                spiritual lives, as well as those of the next generation.
              </p>
            </article>

            <article className="statement-panel statement-panel-mission">
              <p className="section-kicker">What we practice</p>
              <h2>Music Mission</h2>
              <p>
                Our mission is to welcome and embrace every person who enters
                our doors, whether they think of themselves as singers or not.
                By making music together, we draw one another into the work of
                the Spirit already present among us. Through the practice of
                music, we develop deep listening and sustained attention that
                move our hearts toward loving compassion, humble adoration, and
                heartfelt devotion to God.
              </p>
              <p>
                As we sing and make music together faithfully, week after week,
                we reinvigorate the musical life of the Church and continue its
                great tradition of musical expression. What we practice together
                becomes the musical world our children inherit. <em>Soli Deo
                Gloria</em> becomes the song of our hearts, and through God’s
                grace, we give voice here on earth to the music of heaven.
              </p>
            </article>
          </section>

          <section
            id="sunday-worship"
            className="sunday-center"
            aria-labelledby="sunday-worship-heading"
          >
            <div className="sunday-center-heading">
              <p className="section-kicker">The center</p>
              <h2 id="sunday-worship-heading">
                Sunday Worship: The Heart of Our Musical Life
              </h2>
            </div>
            <div className="sunday-center-body">
              <p className="sunday-center-lead">
                The congregation is the principal musical body of the Church.
              </p>
              <p>
                Sunday worship is the chief expression of our musical life
                because it is where the whole congregation gathers and sings.
                The congregation is not an audience for the choir, the
                instrumentalists, or the Music Director. It is the place where
                every voice joins in the Church’s common prayer.
              </p>
              <p>
                Every ensemble, rehearsal, lesson, special project, and part of
                the Music Director’s work exists to nurture and strengthen that
                shared song. Some activities serve Sunday worship directly.
                Others cultivate the listening, skill, devotion, leadership,
                and relationships from which it grows. Every part of our musical
                life finds its purpose in helping the Church sing together.
              </p>
            </div>
          </section>

          <section
            id="formation"
            className="formation-section"
            aria-labelledby="formation-heading"
          >
            <div className="formation-intro">
              <div>
                <p className="section-kicker">A deeper foundation</p>
                <h2 id="formation-heading">Why Practice Forms Us</h2>
                <p className="formation-epigraph">
                  “Tune my heart to sing thy grace.”
                </p>
              </div>
              <div className="formation-copy">
                <p>
                  The vision above rests on a practical conviction: music does
                  not merely express a spiritual life that has already been
                  formed. Repeated singing helps form attention, memory, desire,
                  judgment, and our ability to act with other people. We
                  understand congregational song as one of the practices through
                  which God tunes our hearts.
                </p>
                <p>
                  Older writers describe this formation from different angles.
                  Plato says rhythm and harmony reach into the soul before reason
                  can explain them. Aristotle says judgment grows through
                  participation. Athanasius and Basil describe sung words
                  bringing the soul into proportion and carrying teaching
                  through delight. Aristides, Augustine, and Iamblichus examine
                  music as a practice of the passions, inward measure, and daily
                  attention. They do not offer one doctrine, but together they
                  help us notice that music works upon the whole person.
                </p>
              </div>
            </div>

            <div className="formation-principles">
              {formationPrinciples.map((principle) => (
                <article className="formation-principle" key={principle.title}>
                  <p className="formation-sources">{principle.sources}</p>
                  <h3>{principle.title}</h3>
                  <p>{principle.text}</p>
                </article>
              ))}
            </div>

            <div className="formation-application">
              <p>
                At St. Luke’s ~ San Lucas, Sunday singing is the musical practice
                open to everyone. Anyone may sing here. Those who join the choir
                or another musical group undertake the training, attention, and
                presence that help make that shared freedom possible. Their skill
                is not a higher tier of belonging. It is service offered so that
                the congregation can sing.
              </p>
              <a
                className="formation-reader-link"
                href="https://philology.datu.dev/music/"
                target="_blank"
                rel="noreferrer"
              >
                <span>Read the Greek and Latin sources</span>
                <small>
                  Seven short passages with translation and a word-by-word layer
                </small>
              </a>
            </div>
          </section>

          <section id="ministries" className="ministries-section" aria-labelledby="ministries-heading">
            <div className="section-heading-block">
              <p className="section-kicker">One mission, many practices</p>
              <h2 id="ministries-heading">Our Musical Ministries</h2>
              <p>
                Each ministry has its own work, rhythm, and responsibilities,
                but none is a separate end in itself. Each one returns something
                essential to the congregation’s song. These brief descriptions
                are the first layer; each ministry will be expanded with its own
                mission and clear expectations for those who take part.
              </p>
            </div>

            <div className="ministries-grid">
              {ministries.map((ministry, index) => (
                <article className="ministry-card" key={ministry.name}>
                  <p className="ministry-number" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  <h3>{ministry.name}</h3>
                  <p>{ministry.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section
            id="shared-shape"
            className="shared-shape-section"
            aria-labelledby="shared-shape-heading"
          >
            <div className="section-heading-block">
              <p className="section-kicker">The next layer</p>
              <h2 id="shared-shape-heading">A Common Shape for Every Ministry</h2>
              <p>
                As this page grows, every group will be described through the
                same five questions. This keeps practical expectations rooted
                in the common purpose they serve.
              </p>
            </div>

            <ol className="shared-shape-list">
              {ministryFramework.map((item) => (
                <li key={item.label}>
                  <h3>{item.label}</h3>
                  <p>{item.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <footer className="music-ministry-footer">
            <p>
              <em>Soli Deo Gloria</em>
              <span>To God alone be the glory.</span>
            </p>
            <Link to="/">Return to Liturgical Music</Link>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default MusicMinistryPage;
